import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap'
import { useAuth } from '../../_helper/Auth/AuthProvider'
import { StudentTopMenuLayout } from '../../Layout/StudentTopMenu/StudentTopMenuLayout'
import { AwardTracker, GoalTabPane, Submission } from '../GoalTracker'
import { getStudentIdData, addData, updateData, deleteIdData as deleteGoal } from '../../lib/goalstracker'
import { getStudentRegisterDate, upsertStudentRegisterDate } from '../../lib/goalstrackerdate'
import { getGoalStrackerIdData, addData as addFollowData, updateData as updateFollowData, deleteIdData } from '../../lib/goalstrackerfollow'
import { ToastNotification } from '../Lessons/CourseReport/CourseReportComponents'

const congressionalTabConfig = [
	{ id: 'programdetails', label: 'Program Details', type: 'custom' },
	{ id: 'volunteering', label: 'Volunteering', type: 'goal' },
	{ id: 'personal_development', label: 'Personal Development', type: 'goal' },
	{ id: 'physical_fitness', label: 'Physical Fitness', type: 'goal' },
	{ id: 'exploration', label: 'Exploration', type: 'goal' },
	{ id: 'submission', label: 'Submission', type: 'custom' }
]

const Student911CongressionalAwards = () => {
	const { studentId } = useParams()
	const { user } = useAuth()
	const isInstructorView = user?.role === 'Instructor'
	const effectiveStudentId = isInstructorView ? studentId : user?.id
	const [activeTab, setActiveTab] = useState('volunteering')
	const [goals, setGoals] = useState({
		volunteering: [],
		personal_development: [],
		physical_fitness: [],
		exploration: []
	})
	const [toastMessage, setToastMessage] = useState('')
	const [showToast, setShowToast] = useState(false)
	const [selectedRegisterDate, setSelectedRegisterDate] = useState(() => {
		const today = new Date()
		return today.toISOString().split('T')[0]
	})
	const [updateRegisterDatePending, setUpdateRegisterDatePending] = useState(false)

	const toggle = (tab) => {
		if (activeTab !== tab) setActiveTab(tab)
	}

	useEffect(() => {
		const initializeGoals = async () => {
			try {
				const existingGoals = await getStudentIdData(effectiveStudentId)
				if (existingGoals.length > 0) {
					const goalsWithTrackerData = await Promise.all(
						existingGoals.map(async (goal) => {
							const activities = await getGoalStrackerIdData(goal.id)
							return { ...goal, activities }
						})
					)

					const newGoals = {
						volunteering: goalsWithTrackerData.filter((goal) => goal.trackertype === 'volunteering').sort((a, b) => a.id - b.id),
						personal_development: goalsWithTrackerData.filter((goal) => goal.trackertype === 'personal_development').sort((a, b) => a.id - b.id),
						physical_fitness: goalsWithTrackerData.filter((goal) => goal.trackertype === 'physical_fitness').sort((a, b) => a.id - b.id),
						exploration: goalsWithTrackerData.filter((goal) => goal.trackertype === 'exploration').sort((a, b) => a.id - b.id)
					}

					setGoals(newGoals)
				}
			} catch (error) {
				console.error('Error initializing goals:', error)
			}
		}

		if (effectiveStudentId) {
			initializeGoals()
		}
	}, [effectiveStudentId])

	useEffect(() => {
		const loadSavedRegisterDate = async () => {
			if (!effectiveStudentId) return
			try {
				const saved = await getStudentRegisterDate(effectiveStudentId)
				if (saved) {
					setSelectedRegisterDate(saved)
				}
			} catch (error) {
				console.error('Error loading goalstrackerdate:', error)
			}
		}
		loadSavedRegisterDate()
	}, [effectiveStudentId])

	const showToastMessage = (message) => {
		setToastMessage(message)
		setShowToast(true)
		setTimeout(() => setShowToast(false), 3000)
	}

	const handleGoalUpdate = async (goalType, index, field, value) => {
		const updatedGoals = [...goals[goalType]]
		updatedGoals[index][field] = value
		setGoals({ ...goals, [goalType]: updatedGoals })

		const { activities, ...goalData } = updatedGoals[index]
		await updateData(goalData)
		showToastMessage('Goal updated successfully!')
	}

	const handleActivityUpdate = async (goalType, goalIndex, activityIndex, field, value) => {
		const updatedGoals = [...goals[goalType]]
		updatedGoals[goalIndex].activities[activityIndex][field] = value
		setGoals({ ...goals, [goalType]: updatedGoals })

		await updateFollowData(updatedGoals[goalIndex].activities[activityIndex])
		showToastMessage('Activity updated successfully!')
	}

	const addActivity = async (goalType, goalIndex) => {
		const newActivity = {
			gtid: goals[goalType][goalIndex].id,
			fdate: null,
			fhour: null,
			fnotes: ''
		}

		const response = await addFollowData(newActivity)
		const updatedGoals = [...goals[goalType]]
		updatedGoals[goalIndex] = {
			...updatedGoals[goalIndex],
			activities: [...(updatedGoals[goalIndex].activities || [])]
		}
		updatedGoals[goalIndex].activities.push(response[0])
		setGoals({ ...goals, [goalType]: updatedGoals })
	}

	const formatDateForDisplay = (isoDate) => {
		if (!isoDate) return ''
		const [year, month, day] = isoDate.split('-')
		return `${month}/${day}/${year}`
	}

	const addNewGoal = async (goalType) => {
		const newGoal = {
			studentId: effectiveStudentId,
			goaldesc: '',
			goaltype: '',
			goalcontact: '',
			trackertype: goalType,
			registerdate: formatDateForDisplay(selectedRegisterDate) || new Date().toLocaleDateString('en-US', {
				month: '2-digit',
				day: '2-digit',
				year: 'numeric'
			})
		}

		const response = await addData(newGoal)
		setGoals((prevGoals) => ({
			...prevGoals,
			...prevGoals,
			[goalType]: [...prevGoals[goalType], response[0]]
		}))
		showToastMessage('Goal added successfully!')
	}

	const deleteGoalWithActivities = async (goalType, goalIndex) => {
		const goal = goals[goalType][goalIndex]

		if (goal.activities && goal.activities.length > 0) {
			await Promise.all(goal.activities.map((activity) => deleteIdData(activity.id)))
		}

		await deleteGoal(goal.id)

		setGoals((prevGoals) => ({
			...prevGoals,
			...prevGoals,
			[goalType]: prevGoals[goalType].filter((_, index) => index !== goalIndex)
		}))
		showToastMessage('Goal deleted successfully!')
	}

	const handleUpdateRegisterDate = async () => {
		if (!effectiveStudentId || !selectedRegisterDate) {
			showToastMessage('Student or date is missing.')
			return
		}
		setUpdateRegisterDatePending(true)
		try {
			await upsertStudentRegisterDate(effectiveStudentId, selectedRegisterDate)
			showToastMessage('Register date saved successfully!')
		} catch (error) {
			console.error('Error saving goalstrackerdate:', error)
			showToastMessage(error.message || 'Failed to save register date.')
		} finally {
			setUpdateRegisterDatePending(false)
		}
	}

	const removeActivity = async (goalType, goalIndex, activityIndex, activityId) => {
		await deleteIdData(activityId)

		const updatedGoals = [...goals[goalType]]
		updatedGoals[goalIndex].activities = updatedGoals[goalIndex].activities.filter((_, idx) => idx !== activityIndex)
		setGoals({ ...goals, [goalType]: updatedGoals })
	}

	return (
		<StudentTopMenuLayout activeMenu="Awards" pageName="Congressional Awards">
			<ToastNotification show={showToast} message={toastMessage} onClose={() => setShowToast(false)} />
			<div className="card mb-4 shadow-sm" style={{ borderRadius: '8px' }}>
				{/*}
				<div className="m-3 border-bottom d-flex justify-content-between align-items-center">
					<div className="d-flex align-items-center mb-3">						
						<h5 className="mb-0 d-inline-block">Congressional Awards</h5>
					</div>
				</div>
					{*/}
				<div className="collapse show" id="collapseCongressionalAwards">
					<div className="card-body">
						<Nav tabs>
							{congressionalTabConfig.map((tab) => (
								<NavItem key={tab.id}>
									<NavLink className={activeTab === tab.id ? 'active' : ''} onClick={() => toggle(tab.id)} style={{ cursor: 'pointer' }}>
										{tab.label}
									</NavLink>
								</NavItem>
							))}
						</Nav>

						<TabContent activeTab={activeTab} className="mt-3">
							{congressionalTabConfig.map((tab) => {
								if (tab.type === 'goal') {
									return (
										<GoalTabPane
											key={tab.id}
											tabId={tab.id}
											goals={goals[tab.id]}
											goalType={tab.id}
											selectedRegisterDate={selectedRegisterDate}
											onRegisterDateChange={setSelectedRegisterDate}
											onUpdateRegisterDate={handleUpdateRegisterDate}
											updateRegisterDatePending={updateRegisterDatePending}
											onGoalUpdate={handleGoalUpdate}
											onActivityUpdate={handleActivityUpdate}
											onAddActivity={addActivity}
											onDeleteGoal={deleteGoalWithActivities}
											onRemoveActivity={removeActivity}
											onAddNewGoal={addNewGoal}
										/>
									)
								}

								if (tab.id === 'programdetails') {
									return (
										<TabPane key={tab.id} tabId={tab.id}>
											<AwardTracker />
										</TabPane>
									)
								}

								if (tab.id === 'submission') {
									return (
										<TabPane key={tab.id} tabId={tab.id}>
											{goals.volunteering.length > 0 ||
											goals.personal_development.length > 0 ||
											goals.physical_fitness.length > 0 ||
											goals.exploration.length > 0 ? (
												<Submission
													volunteeringGoals={goals.volunteering}
													personalDevelopmentGoals={goals.personal_development}
													physicalFitnessGoals={goals.physical_fitness}
													explorationGoals={goals.exploration}
												/>
											) : (
												<p>Loading...</p>
											)}
										</TabPane>
									)
								}

								return null
							})}
						</TabContent>
					</div>
				</div>
			</div>
		</StudentTopMenuLayout>
	)
}

export { Student911CongressionalAwards }
